import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Motion } from '#pplx/frontend/aether/components/Motion/index.js';
import { useAskInputSearchModeModelController } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputSearchModeModelController.js';
import { resolveAskInputSubmitButtonPolicy } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/submission-policy/submitButtonPolicy.js';
import { useAskInputActions } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { DictationButton, DictationStopButton, DictationSubmitButton, } from '#pplx/frontend/apps/ask/ask-input/ask-input-voice/components/Dictation.js';
import { StopButton } from '#pplx/frontend/apps/ask/ask-input/ask-input-voice/components/StopButton.js';
import { useStopGeneratingShortcut } from '#pplx/frontend/apps/ask/ask-input/ask-input-voice/components/StopButtonShared.js';
import { VoiceToVoiceButton } from '#pplx/frontend/apps/ask/ask-input/ask-input-voice/components/VoiceToVoiceButton.js';
import { useOpenVoiceToVoice } from '#pplx/frontend/apps/ask/ask-input/ask-input-voice/hooks/useOpenVoiceToVoice.js';
import { AskInputModelSelector } from '#pplx/frontend/apps/ask/ask-input/model-selector/AskInputModelSelector.js';
import { resolveModelPickerControl } from '#pplx/frontend/apps/ask/ask-input/model-selector/resolveModelPickerControl.js';
import { useAskInputModelSelectorUrlIntent, useAskInputWindowsVoiceUrlIntents, } from '#pplx/frontend/apps/ask/ask-input/multimodal-url-intents/useAskInputUrlIntents.js';
import { useIsStreamingQueryCapableOfClarifyingQuestions } from '#pplx/frontend/apps/ask/ask-input/streaming-controls/useIsStreamingQueryCapableOfClarifyingQuestions.js';
import { isAskInputVoiceToVoiceEnabled } from '#pplx/frontend/apps/ask/ask-input/voice-controls/isAskInputVoiceToVoiceEnabled.js';
import { submitAskInputDictation } from '#pplx/frontend/apps/ask/ask-input/voice-controls/submitAskInputDictation.js';
import { useAskInputNativeVoiceEvents } from '#pplx/frontend/apps/ask/ask-input/voice-controls/useAskInputNativeVoiceEvents.js';
import { useOpenVoiceToVoiceModal } from '#pplx/frontend/apps/ask/ask-input/voice-controls/useOpenVoiceToVoiceModal.js';
import { useVoiceModeImplementation } from '#pplx/frontend/apps/ask/ask-input/voice-controls/voiceModeImplementationShared.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useGateSearchModelSelection } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useGateSearchModelSelection.js';
import { useTextOnlyModelHandler } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useTextOnlyModelHandler.js';
import { useWebVoiceAction } from '#pplx/frontend/apps/ask/multimodal/voice/launcher/useWebVoiceAction.js';
import { ComputerModelChip } from '#pplx/frontend/apps/ask/search-models-menu/ComputerModelChip.js';
import { usePreferredSearchModelForSearchMode } from '#pplx/frontend/apps/ask/search/hooks/usePreferredSearchModelForSearchMode.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { RightAttribution } from '#pplx/frontend/components/form/ResizableInput/RightAttribution.js';
import { hasCurrentImageUpload } from '#pplx/frontend/components/utils/fileUploadUtils.js';
import { SubmitButton } from '#pplx/frontend/composer-sdk/components/SubmitButton.js';
import { ComposerContainerContext } from '#pplx/frontend/composer-sdk/ComposerContainerContext.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { noop } from 'lodash-es';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
const AskInputRightAttributionSubmitButton = ({ rounded, toolTip, icon, disabled, isLoading, disabledTooltip, querySource, onSubmit, }) => {
    const { $t } = useIntl();
    const submitLabel = toolTip ||
        $t({
            defaultMessage: 'Submit',
            description: 'Aria label for the submit button in the ask input',
            id: '579af8c135',
        });
    return (_jsx(SubmitButton, { icon: icon, label: submitLabel, disabled: disabled, isLoading: isLoading, disabledTooltip: disabledTooltip, rounded: rounded, onClick: () => onSubmit({ querySource }) }));
};
function VoiceOrSubmitButton({ voice, onVoiceClick, ...submitProps }) {
    const openVoice = useOpenVoiceToVoice();
    const action = useWebVoiceAction({ fallback: onVoiceClick ?? openVoice });
    const voiceMode = useVoiceModeImplementation();
    if (voice && voiceMode)
        return _jsx(VoiceToVoiceButton, { pill: true, onClick: onVoiceClick });
    return (_jsx(AskInputRightAttributionSubmitButton, { ...submitProps, icon: voice ? action.icon : submitProps.icon, toolTip: voice ? action.label : submitProps.toolTip, disabled: voice ? false : submitProps.disabled, isLoading: voice ? false : submitProps.isLoading, disabledTooltip: voice ? undefined : submitProps.disabledTooltip, onSubmit: voice ? action.onPress : submitProps.onSubmit }));
}
const DEFAULT_COMPUTER_MODEL_CHIP_PROPS = {
    showComputerModelChip: false,
};
const AskInputRightAttributionBase = ({ value, json, isFollowUp, isDisabled, isSubmitLoading = false, disabledSubmitTooltip, querySource, handleSubmit, showStopButton, isAskInputFocused, inFlightEntryUUID, showModelSelector, modelSelectorOverrides, onStopButtonClick, disableActionButtons, 
// voice dictation props
startTranscription, stopTranscription, isTranscribing, isTranscriptionInitializing, isTranscriptionAvailable, transcriptionError = null, uploadedFiles, hasQuery, isCometHome, isMissionControl, showVoice = true, leadingRightAttribution, additionalRightAttribution, showSubmit = true, onVoiceClick, searchModel, onSearchModelSelect, onSearchModelDeselect, computerEffortPreferences, computerEffortEnabled, onComputerEffortPreferencesChange, skipSearchModeGate = false, computerModelChipProps = DEFAULT_COMPUTER_MODEL_CHIP_PROPS, searchModelMenuLocalMode, searchModelTriggerAccessory, }) => {
    const { isMobileStyle, isMobileUserAgent } = useIsMobile();
    const { onActiveMenuChange } = useAskInputActions();
    const { device: { isWindowsApp }, } = useWebPlatform();
    const { track } = useInstrumentation();
    const valueRef = useRef(value);
    const jsonRef = useRef(json);
    // Never refer to the value directly, so that we can avoid re-rendering
    valueRef.current = value;
    jsonRef.current = json;
    const isQueryCapableOfClarifyingQuestions = useIsStreamingQueryCapableOfClarifyingQuestions();
    // When the swap logic below replaces the visible <StopButton> with a submit
    // button (Computer enqueues a follow-up, or RESEARCH/ASI in-context
    // clarification), the button's own Esc shortcut goes away with it. Register
    // an equivalent shortcut here so Esc keeps cancelling. Gated on
    // submitDuringStreaming to avoid double-registering when the Stop button is
    // mounted — that would shadow the button's in-flight guard and loading spinner.
    const hasDraft = hasQuery || !!uploadedFiles?.length;
    const hasAttachments = !!uploadedFiles?.length;
    const submitDuringStreaming = hasDraft && (isQueryCapableOfClarifyingQuestions || !isDisabled);
    useStopGeneratingShortcut({
        onStop: onStopButtonClick,
        isEnabled: showStopButton && isAskInputFocused && submitDuringStreaming,
    });
    const openPaywallUpsell = usePaywallUpsell();
    const { getPreferredSearchModelForSearchMode } = usePreferredSearchModelForSearchMode();
    const { gateSearchModeSelection } = useGateSearchModelSelection({ openPaywallUpsell });
    const { searchModelProps, selectedSearchMode, selectedModel, setSelectedModel: handleSetSelectedSearchModel, } = useAskInputSearchModeModelController({
        searchModel,
        onSearchModelSelect,
        onSearchModelDeselect,
    });
    const { supportsModelSwitching } = useSearchModels({ reason: 'computer-model-chip' });
    const modelPickerControl = resolveModelPickerControl(computerModelChipProps.showComputerModelChip
        ? {
            kind: 'computer-orchestrator',
            orchestrator: computerModelChipProps.model,
            isModelSelectionEnabled: searchModelProps.enabled,
            computerEffortPreferences,
            supportsModelSwitching,
        }
        : {
            kind: 'mode-model',
            showModelSelector,
            isModelSelectionEnabled: searchModelProps.enabled,
            selectedSearchMode,
        });
    const handleSubmitValue = useCallback(({ querySource }) => {
        const decision = resolveAskInputSubmitButtonPolicy({
            gateSearchModeSelection,
            getPreferredSearchModelForSearchMode,
            hasAttachments,
            isFollowUp,
            querySource,
            selectedSearchMode,
            showStopButton,
            skipSearchModeGate,
        });
        track(decision.analyticsEvent);
        if (!decision.accepted) {
            return;
        }
        handleSubmit({
            attempt: { method: 'pointer', fromQueue: false },
            query: valueRef.current,
            json: jsonRef.current,
            querySource,
        });
    }, [
        handleSubmit,
        selectedSearchMode,
        gateSearchModeSelection,
        getPreferredSearchModelForSearchMode,
        skipSearchModeGate,
        track,
        isFollowUp,
        hasAttachments,
        showStopButton,
    ]);
    const openVoiceToVoiceModal = useOpenVoiceToVoiceModal();
    useAskInputWindowsVoiceUrlIntents({
        isWindowsApp,
        onStartDictation: startTranscription,
        onOpenVoiceToVoice: openVoiceToVoiceModal,
    });
    useAskInputModelSelectorUrlIntent(onActiveMenuChange);
    useAskInputNativeVoiceEvents({
        isTranscribing,
        isTranscriptionAvailable,
        onStartTranscription: startTranscription,
        onStopTranscription: stopTranscription,
        onOpenVoiceToVoice: openVoiceToVoiceModal,
    });
    const voiceMode = useVoiceModeImplementation();
    const v2vEnabled = isAskInputVoiceToVoiceEnabled({
        showVoice: showVoice && (voiceMode?.isAvailable ?? true),
        isMissionControl,
        isFollowUp,
        isCometHome,
        isMobileUserAgent,
        isMobileStyle,
        sidecarBuildFlag: import.meta.env.VITE_IS_SIDECAR_BUILD,
    });
    const { isFramed } = useContext(ComposerContainerContext);
    const mainIcon = (!!import.meta.env.VITE_IS_SIDECAR_BUILD || isFollowUp ? $icon('arrow-up') : $icon('arrow-right'));
    // Determine which buttons to show based on the current state
    const renderActionButtons = () => {
        // Active dictation keeps its Stop control; voice cannot have started over it.
        const isDictating = isTranscriptionAvailable && isTranscribing;
        if (showVoice !== false && voiceMode?.isActive && !isDictating) {
            return _jsx(VoiceToVoiceButton, { pill: true });
        }
        // If disableActionButtons, show a disabled submit button
        if (disableActionButtons) {
            return (_jsx(AskInputRightAttributionSubmitButton, { disabled: true, disabledTooltip: disabledSubmitTooltip, icon: mainIcon, querySource: querySource ?? 'home', onSubmit: noop, rounded: true }));
        }
        const submitButton = querySource && showSubmit && (_jsx(AskInputRightAttributionSubmitButton, { icon: mainIcon, disabled: isDisabled, isLoading: isSubmitLoading, disabledTooltip: disabledSubmitTooltip, querySource: querySource, onSubmit: handleSubmitValue, rounded: true }, "submit-button"));
        const buttons = [];
        if (isTranscriptionAvailable && isTranscribing) {
            buttons.push(_jsx(DictationStopButton, { stopTranscription: stopTranscription, pill: true }, "dictation-stop-button"));
            if (querySource) {
                buttons.push(_jsx(DictationSubmitButton, { onSubmit: handleDictationSubmit, pill: true }, "dictation-submit-button"));
            }
        }
        const dictationButton = isTranscriptionAvailable && !isTranscribing && (_jsx(DictationButton, { isInitializing: isTranscriptionInitializing, startTranscription: startTranscription, error: transcriptionError, disabled: showStopButton, pill: true }, "dictation-button"));
        if (showStopButton) {
            // Show submit during streaming only when it's actually actionable on
            // this surface (Computer enqueues, RESEARCH/ASI uses in-context
            // clarification). On surfaces where submit is disabled while streaming
            // (Ask follow-up), fall through to Stop so the user has a working
            // action and can cancel with Esc.
            if (submitDuringStreaming && submitButton) {
                buttons.push(submitButton);
            }
            else {
                buttons.push(_jsx(Motion.Slide, { isVisible: true, children: _jsx(StopButton, { onStopButtonClick: onStopButtonClick, isAskInputFocused: isAskInputFocused, entryId: inFlightEntryUUID, pill: true }) }, "stop-button"));
            }
        }
        else if (v2vEnabled) {
            const shouldShowSubmit = (uploadedFiles?.length || hasQuery) && querySource && !isTranscribing;
            const shouldShowV2V = !isTranscribing && !shouldShowSubmit;
            buttons.push(...[dictationButton].filter(Boolean));
            if (isFramed && (shouldShowV2V || (shouldShowSubmit && submitButton))) {
                buttons.push(_jsx(VoiceOrSubmitButton, { voice: shouldShowV2V, onVoiceClick: onVoiceClick, icon: mainIcon, disabled: isDisabled, isLoading: isSubmitLoading, disabledTooltip: disabledSubmitTooltip, querySource: querySource ?? 'home', onSubmit: handleSubmitValue, rounded: true }, "primary-action"));
            }
            else if (shouldShowV2V) {
                buttons.push(_jsx(VoiceToVoiceButton, { pill: true, onClick: onVoiceClick }, "voice-to-voice-button"));
            }
            else if (shouldShowSubmit && submitButton) {
                buttons.push(submitButton);
            }
        }
        else if (!isTranscribing) {
            buttons.push(...[dictationButton, submitButton].filter(Boolean));
        }
        return isTranscriptionAvailable || v2vEnabled ? buttons : submitButton;
    };
    const handleDictationSubmit = useCallback(() => {
        submitAskInputDictation({
            hasQuery,
            onStopTranscription: stopTranscription,
            onSubmit: () => {
                handleSubmit({
                    attempt: { method: 'dictation', fromQueue: false },
                    query: valueRef.current,
                    json: jsonRef.current,
                    querySource: querySource,
                });
            },
        });
    }, [stopTranscription, hasQuery, handleSubmit, querySource]);
    const hasImageAttachments = hasCurrentImageUpload(uploadedFiles);
    // Handle switching away from text-only models when images are uploaded
    useTextOnlyModelHandler({
        hasImageAttachments,
        selectedModel,
        onModelSwitch: handleSetSelectedSearchModel,
    });
    // Determines which models are enabled for selection
    const modelRestrictions = useMemo(() => ({ allowTextOnlyModels: !hasImageAttachments }), [hasImageAttachments]);
    if (isTranscribing) {
        return (_jsxs(RightAttribution, { children: [leadingRightAttribution, additionalRightAttribution, querySource && renderActionButtons()] }));
    }
    return (_jsxs(RightAttribution, { children: [leadingRightAttribution, (modelSelectorOverrides !== undefined || modelPickerControl.kind === 'dropdown') && (_jsx("div", { className: "-mr-2 inline-flex", children: _jsx(AskInputModelSelector, { querySource: querySource, restrictions: modelRestrictions, localMode: searchModelMenuLocalMode, triggerAccessory: searchModelTriggerAccessory, computerEffortPreferences: computerEffortPreferences, computerEffortEnabled: computerEffortEnabled, onComputerEffortPreferencesChange: onComputerEffortPreferencesChange, ...searchModelProps, ...modelSelectorOverrides, searchMode: selectedSearchMode, trackModelSelection: modelPickerControl.kind === 'dropdown' && modelPickerControl.trackModelSelection }) })), modelSelectorOverrides === undefined && modelPickerControl.kind === 'chip' && (_jsx(ComputerModelChip, { model: modelPickerControl.model })), additionalRightAttribution, querySource && renderActionButtons()] }));
};
const AskInputRightAttribution = React.memo(AskInputRightAttributionBase);
export { AskInputRightAttribution };
//# sourceMappingURL=AskInputRightAttributions.js.map